import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as SearchMagnifyIcon } from '../../../assets/svg/SearchMagnifyIcon.svg';
import { ReactComponent as TargetUndo } from '../../../assets/svg/undoTarget.svg';
import { FaEdit, FaTrash, FaUser } from 'react-icons/fa';
import { deleteLastFinanceCard, getSingleUserById } from '../../../server/server';
import { format } from 'date-fns';
import AddNewFinanceCardModal from './AddNewFinanceCardModal';
import ConfirmationModal from './ConfirmationModal';
import { notif } from '../../utils/util';

function FinanceCard() {
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(true);
    const timeout = useRef<any>(null);
    const { id } = useParams();
    const navigate = useNavigate();
    const [hoveredButton, setHoveredButton] = useState(false);
    const [userProfile, setUserProfile] = useState<any>(null);
    const [addNewCardOpen, setAddNewCardOpen] = useState(false);
    const [deleteFinanceCard, setDeleteFinanceCard] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [filteredKartice, setFilteredKartice] = useState(userProfile?.finansijskaKartica || []);

    useEffect(() => {
        if (!!id) loadUser(id);
    }, [id]);

    const loadUser = async (id: string) => {
        setLoading(true);
        const resp = await getSingleUserById(id);
        if (resp.status === 200) {
            setUserProfile(resp.data);
            setFilteredKartice(resp.data.finansijskaKartica ?? []);
            setLoading(false);
        } else {
            setLoading(false);
        }
    };

    const handleChange = (e: any) => {
        e.preventDefault();
        const value = e.target.value.toLowerCase(); // Tekst za pretragu
        setSearchText(e.target.value);

        if (timeout.current !== null) {
            clearTimeout(timeout.current);
        }

        // Debouncing pretrage - poziva se nakon 700ms neaktivnosti
        timeout.current = setTimeout(() => {
            if (userProfile?.finansijskaKartica) {
                // Filtriranje liste po 'datum' i 'uplata'
                const filtered = userProfile.finansijskaKartica.filter((kartica: any) => {
                    const datumString = kartica.datumUplate
                        ? format(kartica.datumUplate, 'dd.MMM.yyyy') // Datum u string formatu
                        : '';
                    const uplataString = kartica.uplata ? kartica.uplata.toString() : '';

                    return (
                        datumString.toLowerCase().includes(value) || uplataString.toLowerCase().includes(value) // Provera da li se search poklapa
                    );
                });

                setFilteredKartice(filtered);
            }
        }, 700); // Debounce delay
    };

    const reloadList = () => {
        if (!!id) loadUser(id);
    };

    const removeLastFinanceCard = async () => {
        setProcessing(true);
        const resp = await deleteLastFinanceCard(userProfile?.id);
        if (resp.status === 200) {
            reloadList();
            closeDeleteFinanceCardModal();
            notif('Finansijska kartica obrisana');
            setProcessing(false);
        } else {
            setProcessing(false);
            notif('Error');
        }
    };

    const goBack = () => {
        navigate('/admin/usermanagement');
    };

    const openAddNewFinanceCardModal = () => {
        setAddNewCardOpen(true);
    };

    const closeAddNewFinanceCardModal = () => {
        setAddNewCardOpen(false);
    };

    const closeDeleteFinanceCardModal = () => {
        setDeleteFinanceCard(false);
    };

    return (
        <div id="scroll-inf-task" className="flex h-full flex-col items-center overflow-y-auto bg-[#ECEBF8] py-[10px]">
            <div className=" w-full h-full  max-w-[1200px] px-[10px]">
                <div className="w-full min-h-full  bg-white flex flex-col px-[40px] py-[30px] rounded-[6px]">
                    <div>
                        <div className="flex flex-row items-center justify-between">
                            <div
                                className=" flex cursor-pointer mr-5 items-center justify-center bg-blueMedium hover:bg-blueDark  min-w-[56px] w-[56px] h-[56px] rounded-full"
                                onClick={() => goBack()}
                                onMouseEnter={() => setHoveredButton(true)}
                                onMouseLeave={() => setHoveredButton(false)}
                            >
                                <TargetUndo fill={hoveredButton ? '#FDDF6D' : '#FFFFFF'} />
                            </div>
                            <p className="text-gray900 font-bold text-[24px] leading-7">Finansijska kartica</p>
                            <div className="flex flex-row items-center gap-3">
                                <div className="min-w-[40px] h-[40px] rounded-full bg-grey-400 border border-gray600 flex items-center justify-center">
                                    <FaUser width={30} height={30} color="white" />
                                </div>
                                <div>
                                    <p className="text-gray900 font-medium text-[14px] leading-5">{userProfile?.username}</p>
                                    <p className="text-gray600 font-normal text-[14px] leading-5">{userProfile?.email}</p>
                                </div>
                            </div>
                        </div>
                        <div className="mt-8 flex flex-row items-center justify-between">
                            <div className="flex flex-row items-center  gap-2">
                                <p className="text-gray900 font-bold text-[20px] leading-6">Ukupno kartica</p>
                                <p className="text-gray600 font-bold text-[20px] leading-6 ">{!!userProfile ? userProfile?.finansijskaKartica.length : 0}</p>
                            </div>
                            <div className="flex flex-row items-center rounded-full border border-primaryViolet  py-[7px] min-w-[300px] px-4">
                                <input
                                    type="text"
                                    placeholder="Pretraži"
                                    value={searchText}
                                    onChange={(e: any) => handleChange(e)}
                                    className="h-full flex-1 outline-none  text-primaryViolet font-medium text-[14px] leading-5 placeholder:font-medium placeholder:text-gray600"
                                    autoComplete="off"
                                />
                                <SearchMagnifyIcon width={18} height={18} color="#475467" />
                            </div>
                            <div
                                className="flex flex-row gap-2 items-center rounded-[6px] bg-[#EEF4FF] border border-[#C7D7FE] hover:border-[#3538CD]  p-[6px] cursor-pointer transition"
                                onClick={() => openAddNewFinanceCardModal()}
                            >
                                <FaEdit width={20} height={20} color="#3538CD" />
                                <p className="text-[#3538CD] font-semibold text-[14px] leading-5">Dodaj novu karticu</p>
                            </div>
                        </div>
                    </div>
                    {!loading && !!userProfile && filteredKartice.length !== 0 && (
                        <table className="custom-table mt-6 ">
                            <thead>
                                <tr>
                                    <th style={{ width: '15%', borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }}>
                                        <p className="text-gray600 font-semibold text-[14px] leading-5">Datum uplate</p>
                                    </th>
                                    <th>
                                        <p className="text-gray600 font-semibold text-[14px] leading-5">Uplata</p>
                                    </th>
                                    <th>
                                        <p className="text-gray600 font-semibold text-[14px] leading-5">Broj dana</p>
                                    </th>
                                    <th>
                                        <p className="text-gray600 font-semibold text-[14px] leading-5">Početak pretplate</p>
                                    </th>
                                    <th>
                                        <p className="text-gray600 font-semibold text-[14px] leading-5">Važenje pretplate</p>
                                    </th>
                                    <th>
                                        <p className="text-gray600 font-semibold text-[14px] leading-5">Datum kreiranja</p>
                                    </th>
                                    <th style={{ borderTopRightRadius: 8, borderBottomRightRadius: 8, textAlign: 'center' }}>
                                        <p className="text-gray600 font-semibold text-[14px] leading-5">Akcije</p>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredKartice.map((kartica: any, index: number) => (
                                    <tr key={index}>
                                        <td style={{ width: '15%' }}> {format(kartica.datumUplate, 'dd.MMM.yyyy')}</td>
                                        <td>
                                            <p className="text-gray600 font-normal text-[14px] leading-5">{kartica.uplata}</p>
                                        </td>
                                        <td>
                                            <p className="text-gray600 font-normal text-[14px] leading-5">{kartica.brojDana}</p>
                                        </td>
                                        <td>
                                            <p className="text-gray600 font-normal text-[14px] leading-5">{format(kartica.pocetak, 'dd.MMM.yyyy')}</p>
                                        </td>
                                        <td>
                                            <p className="text-gray600 font-normal text-[14px] leading-5">{format(kartica.kraj, 'dd.MMM.yyyy')}</p>
                                        </td>
                                        <td>
                                            <p className="text-gray600 font-normal text-[14px] leading-5">{format(kartica.createdAt, 'dd.MMM.yyyy')}</p>
                                        </td>
                                        <td className="flex items-center justify-center">
                                            {userProfile.finansijskaKartica.length === index + 1 && (
                                                <div
                                                    className="flex flex-row gap-2 items-center rounded-[6px] bg-[#EEF4FF] border hover:border-[#3538CD] border-[#C7D7FE]  p-[6px] cursor-pointer transition"
                                                    onClick={() => setDeleteFinanceCard(true)}
                                                >
                                                    <FaTrash width={20} height={20} color="#3538CD" />
                                                    <p className="text-[#3538CD] font-semibold text-[14px] leading-5">Obriši</p>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            <AddNewFinanceCardModal modalIsOpen={addNewCardOpen} closeModal={closeAddNewFinanceCardModal} user={userProfile} reloadList={reloadList} />
            <ConfirmationModal
                modalIsOpen={deleteFinanceCard}
                closeModal={closeDeleteFinanceCardModal}
                confirmation={removeLastFinanceCard}
                message={'Do you want to delete this card?'}
                subMessage="The user will be permanently deleted."
                processing={processing}
            />
        </div>
    );
}

export default FinanceCard;
